import { useCallback, useEffect, useMemo, useState } from "react";
import * as rxjs from "rxjs";

import {
	type ValuePath,
	type ValuePathSlice,
} from "~/components/inspectorv2/traverseValue/ValuePath";

import { appendGroupedFields, deleteGroupedField, replaceGroupedField } from "./GroupBy";
import { type WherePredicateOperations } from "./Operation";
import { appendOrderedFields, deleteOrderedField, replaceOrderedField } from "./OrderBy";
import {
	type QueryBuilderState,
	emptyQueryBuilderState,
	setCurrQueryNodeEditing,
	setInitialQueryNodeEditing,
} from "./QueryBuilderState";
import { type QueryBuilderStateManager } from "./QueryBuilderStateManager";
import { type QueryNodeType } from "./QueryNodeType";
import { type QueryableField } from "./QueryableField";
import { setFieldSelected, toggleFieldSelected } from "./Select";
import {
	appendWherePredicate,
	deleteWherePredicate,
	replaceArgInWherePredicate,
	replaceFieldInWherePredicate,
	replaceOperationInWherePredicate,
} from "./Where";

export const useQueryBuilderState = () => {
	const [queryBuilderState, setQueryBuilderStateFn] = useState<QueryBuilderState | null>(null);

	/**
	 * this memoizes the function so that it doesn't change on every render
	 * since it is passed through easel context
	 */
	const setQueryBuilderState = useCallback(setQueryBuilderStateFn, [setQueryBuilderStateFn]);

	const stateUpdateChannel = useMemo(() => new rxjs.Subject<QueryBuilderState | null>(), []);
	useEffect(() => {
		stateUpdateChannel.next(queryBuilderState);
	}, [queryBuilderState, stateUpdateChannel]);

	const queryBuilderStateManager = useMemo<QueryBuilderStateManager>(
		() => ({
			useQueryBuilderState: () => {
				const [queryBuilderState, setQueryBuilderState] =
					useState<QueryBuilderState | null>(null);

				useEffect(() => {
					const subscription = stateUpdateChannel.subscribe((state) =>
						setQueryBuilderState(state)
					);
					return () => subscription.unsubscribe();
				}, []);

				return queryBuilderState;
			},
			setQueryActive: (
				cellId: string,
				pathToQueryRoot: ValuePath,
				pathFromQueryRoot: ValuePathSlice,
				queryNodeEditing?: QueryNodeType
			) =>
				setQueryBuilderState(
					emptyQueryBuilderState(
						cellId,
						pathToQueryRoot,
						pathFromQueryRoot,
						queryNodeEditing
					)
				),
			setInitialQueryNodeEditing: (queryNodeEditing: QueryNodeType | undefined) =>
				setQueryBuilderState((s) => setInitialQueryNodeEditing(s, queryNodeEditing)),
			setCurrQueryNodeEditing: (queryNodeEditing: QueryNodeType | null) =>
				setQueryBuilderState((s) => setCurrQueryNodeEditing(s, queryNodeEditing)),
			setToggleFieldSelected: (field: QueryableField) =>
				setQueryBuilderState((s) => toggleFieldSelected(s, field)),
			setFieldSelected: (field: QueryableField, selected: boolean) =>
				setQueryBuilderState((s) => setFieldSelected(s, field, selected)),
			setAppendWherePredicate: (field?: QueryableField, arg?: string) =>
				setQueryBuilderState((s) => appendWherePredicate(s, field, arg)),
			setReplaceFieldInWherePredicate: (index: number, field: QueryableField) =>
				setQueryBuilderState((s) => replaceFieldInWherePredicate(s, index, field)),
			setReplaceOperationInWherePredicate: (index: number, op: WherePredicateOperations) =>
				setQueryBuilderState((s) => replaceOperationInWherePredicate(s, index, op)),
			setReplaceArgInWherePredicate: (index: number, arg: string) =>
				setQueryBuilderState((s) => replaceArgInWherePredicate(s, index, arg)),
			setDeleteWherePredicate: (i: number) =>
				setQueryBuilderState((s) => deleteWherePredicate(s, i)),
			setAppendGroupedFields: (fields: QueryableField[]) =>
				setQueryBuilderState((s) => appendGroupedFields(s, fields)),
			setReplaceGroupedField: (index: number, field: QueryableField) =>
				setQueryBuilderState((s) => replaceGroupedField(s, index, field)),
			setDeleteGroupedField: (index: number) =>
				setQueryBuilderState((s) => deleteGroupedField(s, index)),
			setAppendOrderedFields: (fields: QueryableField[]) =>
				setQueryBuilderState((s) => appendOrderedFields(s, fields)),
			setReplaceOrderedField: (index: number, field: QueryableField) =>
				setQueryBuilderState((s) => replaceOrderedField(s, index, field)),
			setDeleteOrderedField: (index: number) =>
				setQueryBuilderState((s) => deleteOrderedField(s, index)),

			setQueryInactive: () => setQueryBuilderState(null),
		}),
		[setQueryBuilderState, stateUpdateChannel]
	);

	return [queryBuilderState, queryBuilderStateManager] as const;
};
